import { createRichTextController } from "@/components/artifact-editor/rich-text-controller"
import {
  ARTIFACT_TEXT_TOOLBAR_ATTRIBUTE,
  ARTIFACT_TEXT_TOOLBAR_STYLES,
  artifactTextToolbarMarkup,
} from "@/components/artifact-editor/rich-text-toolbar"

export const EDITOR_MESSAGE_TYPES = {
  ready: "slant-editor:ready",
  loaded: "slant-editor:loaded",
  dirty: "slant-editor:dirty",
  blur: "slant-editor:blur",
  history: "slant-editor:history",
  undo: "slant-editor:undo",
  redo: "slant-editor:redo",
  setEditable: "slant-editor:set-editable",
  requestHtml: "slant-editor:request-html",
  html: "slant-editor:html",
} as const

export const EDITOR_CONFIG_ELEMENT_ID = "slant-editor-config"

export interface DocumentEditRuntimeConfig {
  attachments: Record<string, string | null>
  loadId: number
}

export const DOCUMENT_EDIT_RUNTIME_STYLES = `
  html { scroll-padding-top: 48px; }
  body:focus { outline: none; }
  body * { user-select: text !important; -webkit-user-select: text !important; pointer-events: auto !important; }
  body a { cursor: pointer; }
  .slant-editor-hidden { display: none !important; }
  [data-slant-editor][hidden] { display: none !important; }
  ${ARTIFACT_TEXT_TOOLBAR_STYLES}
`

export const DOCUMENT_EDIT_RUNTIME_SCRIPT = `
(function () {
  var EDITOR_ATTR = "data-slant-editor";
  var MARGIN_TEXT_ATTR = "data-slant-margin-text";
  var TYPES = ${JSON.stringify(EDITOR_MESSAGE_TYPES)};
  var SKIP_TAGS = { HTML: 1, HEAD: 1, BODY: 1, STYLE: 1, SCRIPT: 1, TEMPLATE: 1, TITLE: 1, META: 1, LINK: 1, BR: 1, WBR: 1 };
  var HISTORY_LIMIT = 50;
  var REPAGINATE_DELAY_MS = 250;
  var repaginateTimer = null;
  var TEXT_HISTORY_GROUP_MS = 1000;
  var TEXT_INPUT_TYPES = { insertText: 1, insertReplacementText: 1, insertCompositionText: 1, insertFromPaste: 1, deleteContentBackward: 1, deleteContentForward: 1, deleteWordBackward: 1, deleteWordForward: 1, deleteSoftLineBackward: 1, deleteSoftLineForward: 1, deleteHardLineBackward: 1, deleteHardLineForward: 1, deleteByCut: 1, deleteContent: 1, formatBold: 1, formatItalic: 1, formatUnderline: 1, historyUndo: 1, historyRedo: 1 };
  var configElement = document.getElementById(${JSON.stringify(EDITOR_CONFIG_ELEMENT_ID)});
  var config = configElement ? JSON.parse(configElement.textContent || "{}") : {};
  var editable = true;
  var loaded = false;
  var textToolbar = null;
  var richText = null;
  var undoStack = [];
  var redoStack = [];
  var lastHistoryKind = null;
  var lastHistoryAt = 0;

  function post(message) {
    window.parent.postMessage(message, "*");
  }

  function isEditorNode(node) {
    return !!(node && node.nodeType === 1 && node.closest("[" + EDITOR_ATTR + "]"));
  }

  function markDirty() {
    post({ type: TYPES.dirty, dirty: true });
  }

  function stripEditorState(node) {
    Array.prototype.forEach.call(node.querySelectorAll("[" + EDITOR_ATTR + "]"), function (child) { child.remove(); });
    Array.prototype.forEach.call(node.querySelectorAll("[" + MARGIN_TEXT_ATTR + "]"), function (child) { child.removeAttribute(MARGIN_TEXT_ATTR); });
    var images = Array.prototype.slice.call(node.querySelectorAll("[data-slant-src]"));
    if (node.hasAttribute && node.hasAttribute("data-slant-src")) images.push(node);
    images.forEach(function (image) {
      image.setAttribute("src", image.getAttribute("data-slant-src"));
      image.removeAttribute("data-slant-src");
      image.classList.remove("slant-editor-hidden");
    });
  }

  function snapshot() {
    var body = document.body.cloneNode(true);
    window.slantUnpaginate(body);
    return body.innerHTML;
  }

  // The caret's boundary points are copied out before the sheets are rebuilt:
  // a live Range is collapsed by the browser as the nodes around it move.
  function repaginate() {
    var selection = document.getSelection();
    var carets = [];
    if (selection && selection.rangeCount) {
      var current = selection.getRangeAt(0);
      carets = [
        { node: current.startContainer, offset: current.startOffset },
        { node: current.endContainer, offset: current.endOffset },
      ];
    }
    window.slantPaginate({ mode: "screen", carets: carets });
    if (carets.length && carets[0].node.isConnected && carets[1].node.isConnected) {
      var range = document.createRange();
      range.setStart(carets[0].node, Math.min(carets[0].offset, carets[0].node.length || carets[0].node.childNodes.length));
      range.setEnd(carets[1].node, Math.min(carets[1].offset, carets[1].node.length || carets[1].node.childNodes.length));
      selection.removeAllRanges();
      selection.addRange(range);
    }
    updateTextToolbar();
  }

  function scheduleRepaginate() {
    if (repaginateTimer) clearTimeout(repaginateTimer);
    repaginateTimer = setTimeout(function () {
      repaginateTimer = null;
      repaginate();
    }, REPAGINATE_DELAY_MS);
  }

  function postHistory() {
    post({ type: TYPES.history, canUndo: undoStack.length > 0, canRedo: redoStack.length > 0 });
  }

  function recordHistory(kind) {
    var now = Date.now();
    if (kind === "text" && lastHistoryKind === "text" && now - lastHistoryAt < TEXT_HISTORY_GROUP_MS) {
      lastHistoryAt = now;
      return;
    }
    undoStack.push(snapshot());
    if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
    redoStack = [];
    lastHistoryKind = kind;
    lastHistoryAt = now;
    postHistory();
  }

  function restore(html) {
    hideTextToolbar();
    document.body.innerHTML = html;
    window.slantPaginate({ mode: "screen" });
    lastHistoryKind = null;
    markDirty();
    postHistory();
  }

  function undo() {
    if (!undoStack.length) return;
    redoStack.push(snapshot());
    restore(undoStack.pop());
  }

  function redo() {
    if (!redoStack.length) return;
    undoStack.push(snapshot());
    restore(redoStack.pop());
  }

  function imagesDecoded() {
    return Promise.all(
      Array.prototype.map.call(document.images, function (image) {
        return image.decode ? image.decode().catch(function () {}) : Promise.resolve();
      })
    );
  }

  function applyAttachments() {
    var attachments = config.attachments || {};
    Array.prototype.forEach.call(document.querySelectorAll("img[src]"), function (image) {
      var source = image.getAttribute("src");
      if (!(source in attachments)) return;
      image.setAttribute("data-slant-src", source);
      var uri = attachments[source];
      if (uri) image.setAttribute("src", uri);
      else {
        image.removeAttribute("src");
        image.classList.add("slant-editor-hidden");
      }
    });
  }

  // Editing only opens once fonts are ready and the sheets are laid out on
  // the frame's final assignment; until then the flag is just remembered.
  function setEditable(flag) {
    editable = flag;
    if (!loaded) return;
    document.body.setAttribute("contenteditable", editable ? "true" : "false");
    if (!editable) hideTextToolbar();
  }

  function hideTextToolbar() {
    if (!textToolbar) return;
    richText.hide();
  }

  function textBlockFor(node) {
    var element = node && node.nodeType === 1 ? node : node && node.parentElement;
    while (element && element !== document.body) {
      var display = getComputedStyle(element).display;
      if (display !== "inline" && display !== "contents") return element;
      element = element.parentElement;
    }
    return document.body;
  }

  // "start": nothing before the range start in its block. "end": nothing after
  // the range end in its block. "end-start": the range end sits at the very
  // start of its block.
  function atBlockEdge(range, edge) {
    var block = textBlockFor(edge === "start" ? range.startContainer : range.endContainer);
    var probe = document.createRange();
    if (edge === "start") {
      probe.setStart(block, 0);
      probe.setEnd(range.startContainer, range.startOffset);
    } else if (edge === "end-start") {
      probe.setStart(block, 0);
      probe.setEnd(range.endContainer, range.endOffset);
    } else {
      probe.setStart(range.endContainer, range.endOffset);
      probe.setEnd(block, block.childNodes.length);
    }
    return probe.toString().length === 0;
  }

  function normalizeSelection() {
    var selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    var range = selection.getRangeAt(0);
    var startBlock = textBlockFor(range.startContainer);
    var endBlock = textBlockFor(range.endContainer);
    if (startBlock === endBlock || startBlock === document.body || !atBlockEdge(range, "end-start")) return;
    var trimmed = range.cloneRange();
    trimmed.setEnd(startBlock, startBlock.childNodes.length);
    selection.removeAllRanges();
    selection.addRange(trimmed);
  }

  function allowedTextInput(event) {
    var inputType = event.inputType || "";
    if (!TEXT_INPUT_TYPES[inputType]) return false;
    normalizeSelection();
    var selection = document.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    var range = selection.getRangeAt(0);
    if (textBlockFor(range.startContainer) !== textBlockFor(range.endContainer)) return false;
    if (range.collapsed && /^delete/.test(inputType)) {
      if (/Backward$/.test(inputType) && atBlockEdge(range, "start")) return false;
      if (/Forward$/.test(inputType) && atBlockEdge(range, "end")) return false;
    }
    return true;
  }

  function currentTextRange() {
    var selection = document.getSelection();
    if (!editable || !selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
    var range = selection.getRangeAt(0);
    var container = range.commonAncestorContainer;
    var element = container.nodeType === 1 ? container : container.parentElement;
    if (!element || !document.body.contains(element) || isEditorNode(element)) return null;
    if (textBlockFor(range.startContainer) !== textBlockFor(range.endContainer)) return null;
    return range;
  }

  function updateTextToolbar() {
    if (richText) richText.update();
  }

  function installEditor() {
    textToolbar = document.createElement("div");
    textToolbar.setAttribute(EDITOR_ATTR, "text-toolbar");
    textToolbar.setAttribute(${JSON.stringify(ARTIFACT_TEXT_TOOLBAR_ATTRIBUTE)}, "");
    textToolbar.setAttribute("contenteditable", "false");
    textToolbar.hidden = true;
    textToolbar.innerHTML = ${JSON.stringify(artifactTextToolbarMarkup())};
    document.documentElement.appendChild(textToolbar);

    richText = (${createRichTextController.toString()})(document, textToolbar, {
      range: currentTextRange,
      beforeChange: function () { recordHistory("format"); },
      afterChange: function () { markDirty(); scheduleRepaginate(); }
    });
    document.addEventListener("selectionchange", function () {
      if (richText.linkOpen || richText.fontSizeOpen) return;
      if (editable) normalizeSelection();
      updateTextToolbar();
    });
    document.addEventListener("beforeinput", function (event) {
      if (isEditorNode(event.target)) return;
      if (!editable || !allowedTextInput(event)) {
        event.preventDefault();
        return;
      }
      recordHistory(/^format/.test(event.inputType || "") ? "format" : "text");
    });
    document.addEventListener("input", function (event) {
      if (isEditorNode(event.target)) return;
      markDirty();
      scheduleRepaginate();
    });
    document.addEventListener("paste", function (event) {
      if (isEditorNode(event.target)) return;
      event.preventDefault();
      if (!editable || !allowedTextInput({ inputType: "insertFromPaste" })) return;
      var text = event.clipboardData ? event.clipboardData.getData("text/plain") : "";
      text = text.replace(/\\s*\\n+\\s*/g, " ").trim();
      if (!text) return;
      recordHistory("text");
      document.execCommand("insertText", false, text);
    });
    document.addEventListener("dragover", function (event) { event.preventDefault(); });
    document.addEventListener("drop", function (event) { event.preventDefault(); });
    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape") {
        hideTextToolbar();
        return;
      }
      if (!editable || !(event.metaKey || event.ctrlKey)) return;
      var key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        event.preventDefault();
        redo();
      }
    });
  }

  // A click on a link opens it in a new tab as a real link navigation, within
  // the click's own user activation so browsers treat it as the advisor's
  // click; the document's anchor itself never navigates the frame. Dragging
  // to select link text does not open it.
  document.addEventListener("click", function (event) {
    var anchor = event.target.closest && event.target.closest("a");
    if (!anchor) return;
    event.preventDefault();
    var selection = document.getSelection();
    if (selection && !selection.isCollapsed) return;
    var href = anchor.getAttribute("href") || "";
    if (!/^(https?:|mailto:)/i.test(href)) return;
    var opener = document.createElement("a");
    opener.href = href;
    opener.target = "_blank";
    opener.rel = "noopener noreferrer";
    opener.click();
  });
  window.addEventListener("blur", function () {
    post({ type: TYPES.blur });
  });

  // Running headers in @page margin boxes repeat body values such as the
  // title and the prepared date. Each such string is paired with the first
  // body element that carries the same text, and rewritten when that text is
  // edited.
  function pageRuleBlocks(css) {
    var blocks = [];
    var quote = null;
    var depth = 0;
    var pending = false;
    var open = -1;
    var openDepth = 0;
    for (var i = 0; i < css.length; i++) {
      var char = css[i];
      if (quote) {
        if (char === "\\\\") i += 1;
        else if (char === quote) quote = null;
      } else if (char === "/" && css[i + 1] === "*") {
        var close = css.indexOf("*/", i + 2);
        i = close === -1 ? css.length : close + 1;
      } else if (char === '"' || char === "'") {
        quote = char;
      } else if (open === -1 && /^@page\\b/.test(css.substr(i, 6))) {
        pending = true;
        i += 4;
      } else if (char === "{") {
        depth += 1;
        if (pending) {
          open = i;
          openDepth = depth;
          pending = false;
        }
      } else if (char === "}") {
        if (open !== -1 && depth === openDepth) {
          blocks.push(css.slice(open, i + 1));
          open = -1;
        }
        depth -= 1;
      }
    }
    return blocks;
  }

  var MARGIN_LITERAL = /content\\s*:[^;}]*?(["'])((?:\\\\[\\s\\S]|(?!\\1)[^\\\\\\n])*)\\1/g;

  function decodeCssString(raw) {
    return raw
      .replace(/\\\\\\n/g, "")
      .replace(/\\\\([0-9A-Fa-f]{1,6})\\s?/g, function (_, hex) {
        var code = parseInt(hex, 16);
        return code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff) ? String.fromCharCode(65533) : String.fromCodePoint(code);
      })
      .replace(/\\\\(.)/g, "$1");
  }

  function marginBoxLiterals() {
    var css = Array.prototype.map.call(document.querySelectorAll("style"), function (style) { return style.textContent; }).join("\\n");
    var strings = [];
    pageRuleBlocks(css).forEach(function (block) {
      var literal = new RegExp(MARGIN_LITERAL.source, "g");
      var match;
      while ((match = literal.exec(block))) {
        var text = decodeCssString(match[2]);
        if (/[\\p{L}\\p{N}]/u.test(text) && strings.indexOf(text) === -1) strings.push(text);
      }
    });
    return strings;
  }

  // Each running-header literal is bound through an attribute to the deepest
  // element of its first body occurrence, so the binding survives undo and
  // redo and tolerates inline formatting inside the title.
  function pairMarginBoxTexts() {
    var elements = Array.prototype.filter.call(document.body.querySelectorAll("*"), function (element) {
      return !isEditorNode(element) && !SKIP_TAGS[element.tagName];
    });
    marginBoxLiterals().forEach(function (text) {
      var matches = elements.filter(function (candidate) { return candidate.textContent.trim() === text.trim(); });
      var element = matches[0];
      if (!element) return;
      var inner;
      while ((inner = matches.filter(function (candidate) { return candidate.parentElement === element; })[0])) element = inner;
      element.setAttribute(MARGIN_TEXT_ATTR, text);
    });
  }

  function cssString(text, quote) {
    return quote + text.replace(/\\\\/g, "\\\\\\\\").split(quote).join("\\\\" + quote) + quote;
  }

  function syncMarginBoxTexts(root) {
    Array.prototype.forEach.call(root.querySelectorAll("[" + MARGIN_TEXT_ATTR + "]"), function (element) {
      var original = element.getAttribute(MARGIN_TEXT_ATTR);
      var current = element.textContent.trim();
      if (current === original.trim()) return;
      var updated = original.replace(/^(\\s*)[\\s\\S]*?(\\s*)$/, function (_, lead, trail) { return lead + current + trail; });
      Array.prototype.forEach.call(root.querySelectorAll("style"), function (style) {
        pageRuleBlocks(style.textContent).forEach(function (block) {
          var rewritten = block.replace(new RegExp(MARGIN_LITERAL.source, "g"), function (whole, quote, raw) {
            if (decodeCssString(raw) !== original) return whole;
            return whole.slice(0, whole.length - raw.length - 2) + cssString(updated, quote);
          });
          style.textContent = style.textContent.split(block).join(rewritten);
        });
      });
    });
  }

  function serialize() {
    var root = document.documentElement.cloneNode(true);
    window.slantUnpaginate(root);
    syncMarginBoxTexts(root);
    var body = root.querySelector("body");
    if (body) {
      body.removeAttribute("contenteditable");
      body.removeAttribute("spellcheck");
    }
    stripEditorState(root);
    var doctype = document.doctype ? "<!DOCTYPE " + document.doctype.name + ">\\n" : "";
    return doctype + root.outerHTML;
  }

  window.addEventListener("message", function (event) {
    if (event.source !== window.parent) return;
    var data = event.data;
    if (!data) return;
    if (data.type === TYPES.requestHtml) post({ type: TYPES.html, requestId: data.requestId, html: serialize() });
    else if (data.type === TYPES.undo) undo();
    else if (data.type === TYPES.redo) redo();
    else if (data.type === TYPES.setEditable) setEditable(!!data.editable);
  });

  applyAttachments();
  pairMarginBoxTexts();
  Array.prototype.forEach.call(document.body.querySelectorAll("[contenteditable]"), function (element) {
    if (!isEditorNode(element)) element.removeAttribute("contenteditable");
  });
  installEditor();
  document.body.setAttribute("spellcheck", "false");
  post({ type: TYPES.ready, loadId: config.loadId });
  Promise.all([document.fonts ? document.fonts.ready : Promise.resolve(), imagesDecoded()]).then(function () {
    window.slantPaginate({ mode: "screen" });
    loaded = true;
    setEditable(editable);
    postHistory();
    post({ type: TYPES.loaded });
  });
})();
`
